import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import type { MyContributionsCopy } from "./my-contributions-copy";
import type { HistoryFilters as Filters, HistoryStatus } from "./contribution-history";

interface HistoryFiltersProps {
  copy: MyContributionsCopy;
  filters: Filters;
  counts: Record<HistoryStatus | "all", number>;
  languages: string[];
  languageName: (code: string | null) => string;
  format: (value: number) => string;
  onChange: (next: Filters) => void;
  onClear: () => void;
}

/** Status chips with their counts, then language and period. */
export function HistoryFilters({ copy, filters, counts, languages, languageName, format, onChange, onClear }: HistoryFiltersProps) {
  const statuses: Array<{ key: Filters["status"]; label: string; tone: string }> = [
    { key: "all", label: copy.statusAll, tone: "text-foreground" },
    { key: "validated", label: copy.statusValidated, tone: "text-success" },
    { key: "pending", label: copy.statusPending, tone: "text-warning" },
    { key: "rejected", label: copy.statusRejected, tone: "text-danger" },
  ];
  const active = filters.status !== "all" || filters.language !== "all" || Boolean(filters.from) || Boolean(filters.to);

  return (
    <div role="group" aria-label={copy.filtersLabel} className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {statuses.map((status) => {
          const current = filters.status === status.key;
          return (
            <button
              key={status.key}
              type="button"
              aria-pressed={current}
              onClick={() => onChange({ ...filters, status: status.key })}
              className={cn(
                "inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                current
                  ? "border-brand/40 bg-brand/[0.06] font-medium text-foreground"
                  : "border-border/60 text-muted-foreground hover:border-brand/40 hover:text-foreground",
              )}
            >
              {status.label}
              <span className={cn("tabular-nums", current ? status.tone : "text-faint-foreground")}>{format(counts[status.key])}</span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor="mc-filter-language" className="text-xs text-muted-foreground">
            {copy.language}
          </Label>
          <Select value={filters.language} onValueChange={(language) => onChange({ ...filters, language })}>
            <SelectTrigger id="mc-filter-language" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{copy.allLanguages}</SelectItem>
              {languages.map((code) => (
                <SelectItem key={code} value={code}>
                  {languageName(code)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="mc-filter-from" className="text-xs text-muted-foreground">
            {copy.from}
          </Label>
          <Input
            id="mc-filter-from"
            type="date"
            className="h-9"
            value={filters.from}
            max={filters.to || undefined}
            onChange={(event) => onChange({ ...filters, from: event.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="mc-filter-to" className="text-xs text-muted-foreground">
            {copy.to}
          </Label>
          <Input
            id="mc-filter-to"
            type="date"
            className="h-9"
            value={filters.to}
            min={filters.from || undefined}
            onChange={(event) => onChange({ ...filters, to: event.target.value })}
          />
        </div>
        <Button type="button" variant="ghost" size="sm" className="h-9 justify-self-start" disabled={!active} onClick={onClear}>
          <Icon name="x" />
          {copy.clearFilters}
        </Button>
      </div>
    </div>
  );
}
